/**
 * @module components/Settings/EmailSettingsTab
 * @description Pestaña de configuración SMTP para el sistema de notificaciones por email.
 *
 * Permite a los administradores configurar el servidor de email que usa el sistema
 * para enviar notificaciones automáticas (asignaciones, vencimientos, alertas).
 * Soporta múltiples proveedores: Gmail, Outlook, Office 365, SendGrid, Mailgun, Resend, custom SMTP.
 *
 * La configuración SMTP se guarda en la tabla `smtp_settings` de Supabase.
 * Incluye un botón de prueba que envía un email de test antes de guardar.
 *
 * Solo visible para administradores (verificado en SettingsModal antes de renderizar).
 *
 * @see SettingsModal que controla el acceso por rol
 * @see DataContext.queueEmail para cómo se usan estas credenciales SMTP
 */
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Mail, Server, Lock, Send, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';

interface SMTPSettings {
  id?: string;
  provider: 'gmail' | 'outlook' | 'office365' | 'yahoo' | 'sendgrid' | 'mailgun' | 'resend' | 'custom';
  enabled: boolean;
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string;
  smtp_password: string;
  from_email: string;
  from_name: string;
  reply_to: string;
  daily_limit: number;
  hourly_limit: number;
}

const PROVIDER_PRESETS: Record<string, { name: string; host: string; port: number; secure: boolean; instructions: string }> = {
  gmail: {
    name: 'Gmail',
    host: 'smtp.gmail.com',
    port: 587,
    secure: false,
    instructions: 'Usa una App Password de Google. Ve a: Cuenta de Google > Seguridad > Verificación en 2 pasos > Contraseñas de aplicaciones'
  },
  outlook: {
    name: 'Outlook.com',
    host: 'smtp-mail.outlook.com',
    port: 587,
    secure: false,
    instructions: 'Usa tu email y contraseña de Outlook'
  },
  office365: {
    name: 'Office 365',
    host: 'smtp.office365.com',
    port: 587,
    secure: false,
    instructions: 'Usa tu email y contraseña de Office 365'
  },
  yahoo: {
    name: 'Yahoo Mail',
    host: 'smtp.mail.yahoo.com',
    port: 587,
    secure: false,
    instructions: 'Genera una contraseña de aplicación en Configuración de cuenta > Seguridad'
  },
  sendgrid: {
    name: 'SendGrid',
    host: 'smtp.sendgrid.net',
    port: 587,
    secure: false,
    instructions: 'Usuario: "apikey", Contraseña: tu API key de SendGrid'
  },
  resend: {
    name: 'Resend',
    host: 'smtp.resend.com',
    port: 587,
    secure: false,
    instructions: 'Usa tu API key de Resend como contraseña'
  },
  custom: {
    name: 'Personalizado',
    host: '',
    port: 587,
    secure: false,
    instructions: 'Configura tu servidor SMTP personalizado'
  }
};

export const EmailSettingsTab = forwardRef<{ saveChanges: () => Promise<void> }>((props, ref) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const [settings, setSettings] = useState<SMTPSettings>({
    provider: 'gmail',
    enabled: false,
    smtp_host: PROVIDER_PRESETS.gmail.host,
    smtp_port: PROVIDER_PRESETS.gmail.port,
    smtp_secure: PROVIDER_PRESETS.gmail.secure,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: 'Sistema ISO 27001/27002',
    reply_to: '',
    daily_limit: 500,
    hourly_limit: 50
  });

  // Cargar configuración existente
  useEffect(() => {
    loadSettings();
  }, [user?.organization_id]);

  const loadSettings = async () => {
    if (!user?.organization_id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('smtp_settings')
        .select('*')
        .eq('organization_id', user.organization_id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows
        throw error;
      }

      if (data) {
        setSettings(data);
      }
    } catch (error) {
      logger.error('❌ [EMAIL-SETTINGS] Error cargando configuración:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (provider: SMTPSettings['provider']) => {
    const preset = PROVIDER_PRESETS[provider];
    setSettings(prev => ({
      ...prev,
      provider,
      smtp_host: preset.host,
      smtp_port: preset.port,
      smtp_secure: preset.secure
    }));
    setTestResult(null);
  };

  const handleSaveSettings = async () => {
    if (!user?.organization_id) {
      throw new Error('Usuario no autenticado');
    }

    try {
      setLoading(true);

      const dataToSave = {
        organization_id: user.organization_id,
        provider: settings.provider,
        enabled: settings.enabled,
        smtp_host: settings.smtp_host,
        smtp_port: settings.smtp_port,
        smtp_secure: settings.smtp_secure,
        smtp_user: settings.smtp_user,
        smtp_password: settings.smtp_password,
        from_email: settings.from_email,
        from_name: settings.from_name,
        reply_to: settings.reply_to || settings.from_email,
        daily_limit: settings.daily_limit,
        hourly_limit: settings.hourly_limit
      };

      if (settings.id) {
        // Actualizar existente
        const { error } = await supabase
          .from('smtp_settings')
          .update(dataToSave)
          .eq('id', settings.id);

        if (error) throw error;
      } else {
        // Crear nuevo
        const { data, error } = await supabase
          .from('smtp_settings')
          .insert([dataToSave])
          .select()
          .single();

        if (error) throw error;
        setSettings(data);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      logger.log('✅ [EMAIL-SETTINGS] Configuración guardada');
    } catch (error) {
      logger.error('❌ [EMAIL-SETTINGS] Error guardando:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);

      // Llamar Edge Function para testear conexión
      const { data, error } = await supabase.functions.invoke('test-smtp-connection', {
        body: {
          smtp_host: settings.smtp_host,
          smtp_port: settings.smtp_port,
          smtp_secure: settings.smtp_secure,
          smtp_user: settings.smtp_user,
          smtp_password: settings.smtp_password,
          from_email: settings.from_email,
          test_recipient: user?.email || settings.from_email
        }
      });

      if (error) {
        setTestResult({
          success: false,
          message: `Error: ${error.message}`
        });
        return;
      }

      if (data?.success) {
        setTestResult({
          success: true,
          message: 'Conexión exitosa! Se envió un email de prueba.'
        });

        // Actualizar last_test en BD
        if (settings.id) {
          await supabase
            .from('smtp_settings')
            .update({
              last_test_at: new Date().toISOString(),
              last_test_success: true,
              last_test_error: null
            })
            .eq('id', settings.id);
        }
      } else {
        setTestResult({
          success: false,
          message: data?.error || 'Error desconocido al probar conexión'
        });

        // Actualizar last_test en BD
        if (settings.id) {
          await supabase
            .from('smtp_settings')
            .update({
              last_test_at: new Date().toISOString(),
              last_test_success: false,
              last_test_error: data?.error
            })
            .eq('id', settings.id);
        }
      }
    } catch (error) {
      logger.error('❌ [EMAIL-SETTINGS] Error testeando conexión:', error);
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Error desconocido'
      });
    } finally {
      setTesting(false);
    }
  };

  // Expose saveChanges method to parent
  useImperativeHandle(ref, () => ({
    saveChanges: handleSaveSettings
  }));

  if (loading && !settings.id) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  const currentPreset = PROVIDER_PRESETS[settings.provider];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Configuración de Email
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configura las credenciales SMTP para enviar notificaciones desde tu propia cuenta de email
        </p>
      </div>

      {/* Enable/Disable */}
      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">
              Habilitar envío de emails
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Activa el envío de notificaciones por email usando tu configuración SMTP
            </p>
          </div>
        </div>
        <button
          onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            settings.enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              settings.enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Provider Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Proveedor de Email
        </label>
        <select
          value={settings.provider}
          onChange={(e) => handleProviderChange(e.target.value as SMTPSettings['provider'])}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
        >
          {Object.entries(PROVIDER_PRESETS).map(([key, preset]) => (
            <option key={key} value={key}>
              {preset.name}
            </option>
          ))}
        </select>

        {/* Instructions */}
        {currentPreset.instructions && (
          <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex gap-2">
            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              {currentPreset.instructions}
            </p>
          </div>
        )}
      </div>

      {/* SMTP Configuration */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Server className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h4 className="font-medium text-gray-900 dark:text-white">
            Configuración del Servidor SMTP
          </h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Host */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Servidor SMTP
            </label>
            <input
              type="text"
              value={settings.smtp_host}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
              placeholder="smtp.gmail.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Port */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Puerto
            </label>
            <input
              type="number"
              value={settings.smtp_port}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* User */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Usuario / Email
            </label>
            <input
              type="email"
              value={settings.smtp_user}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
              placeholder="tu-email@gmail.com"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Password */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Contraseña / App Password
            </label>
            <input
              type="password"
              value={settings.smtp_password}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
              placeholder="••••••••••••••••"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
            {settings.provider === 'gmail' && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                ⚠️ No uses tu contraseña de Gmail. Genera una App Password en tu cuenta de Google.
              </p>
            )}
          </div>

          {/* Secure */}
          <div className="md:col-span-2 flex items-center gap-2">
            <input
              type="checkbox"
              id="smtp_secure"
              checked={settings.smtp_secure}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_secure: e.target.checked }))}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="smtp_secure" className="text-sm text-gray-700 dark:text-gray-300">
              Usar SSL/TLS (puerto 465)
            </label>
          </div>
        </div>
      </div>

      {/* Email Details */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
          Detalles del Email
        </h4>

        {/* From Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email remitente (FROM)
          </label>
          <input
            type="email"
            value={settings.from_email}
            onChange={(e) => setSettings(prev => ({ ...prev, from_email: e.target.value }))}
            placeholder="iso-compliance@tu-empresa.com"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* From Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre remitente
          </label>
          <input
            type="text"
            value={settings.from_name}
            onChange={(e) => setSettings(prev => ({ ...prev, from_name: e.target.value }))}
            placeholder="Sistema ISO 27001/27002"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Reply To */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email para respuestas (Reply-To) - Opcional
          </label>
          <input
            type="email"
            value={settings.reply_to}
            onChange={(e) => setSettings(prev => ({ ...prev, reply_to: e.target.value }))}
            placeholder={settings.from_email || "no-reply@tu-empresa.com"}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Límites */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2">
          Límites de Envío
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Daily Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Límite diario
            </label>
            <input
              type="number"
              value={settings.daily_limit}
              onChange={(e) => setSettings(prev => ({ ...prev, daily_limit: parseInt(e.target.value) || 500 }))}
              min="1"
              max="10000"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Máximo de emails por día (recomendado: 500)
            </p>
          </div>

          {/* Hourly Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Límite por hora
            </label>
            <input
              type="number"
              value={settings.hourly_limit}
              onChange={(e) => setSettings(prev => ({ ...prev, hourly_limit: parseInt(e.target.value) || 50 }))}
              min="1"
              max="1000"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Máximo de emails por hora (recomendado: 50)
            </p>
          </div>
        </div>
      </div>

      {/* Test Connection */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">
              Probar Conexión
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Envía un email de prueba para verificar la configuración
            </p>
          </div>
          <button
            onClick={handleTestConnection}
            disabled={testing || !settings.smtp_host || !settings.smtp_user || !settings.smtp_password}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {testing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Probando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Probar Conexión
              </>
            )}
          </button>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`p-3 rounded-lg flex items-start gap-2 ${
            testResult.success
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
          }`}>
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className="text-sm font-medium">
                {testResult.success ? '¡Conexión exitosa!' : 'Error en la conexión'}
              </p>
              <p className="text-xs mt-1">
                {testResult.message}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-sm font-medium text-green-900 dark:text-green-100 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Configuración guardada exitosamente
          </p>
        </div>
      )}

      {/* Warning about credentials */}
      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <div className="flex gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
              Seguridad de las credenciales
            </p>
            <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
              <li>• Las contraseñas se almacenan encriptadas en la base de datos</li>
              <li>• Para Gmail y Yahoo, usa App Passwords, no tu contraseña principal</li>
              <li>• Solo administradores pueden ver y modificar esta configuración</li>
              <li>• Las credenciales son únicas por organización</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
});

EmailSettingsTab.displayName = 'EmailSettingsTab';
